/**
 * The ONLY module that touches the MoEngage SDK.
 *
 * - The SDK is never imported statically. `initMoEngage()` schedules a dynamic
 *   `import()` at idle time, so the SDK lands in its own chunk and never
 *   competes with first paint.
 * - Calls made before the SDK is ready are queued (max 200, oldest dropped)
 *   and flushed once it loads. Nothing ever waits on the SDK.
 * - Every SDK call goes through `safeCall`, which swallows sync throws and
 *   async rejections. A MoEngage failure can never break a screen.
 */
import { config } from './config';
import { debugInfo, debugWarn } from './logger';

const MAX_QUEUE = 200;

/** @type {any} */
let sdk = null;
let initCalled = false;
let loading = false;
let failed = false;
/** Set when something else (GTM tag, CDN snippet) already loaded MoEngage. */
let foreignInstance = false;
/** @type {Array<(sdk: any) => any>} */
const queue = [];

/** Run `fn`, swallowing any sync throw or async rejection. Never throws. */
export const safeCall = (fn) => {
    try {
        const result = fn();
        if (result && typeof result.then === 'function') {
            result.then(undefined, (e) => debugWarn('[MoEngage] SDK call rejected', e));
        }
    } catch (e) {
        debugWarn('[MoEngage] SDK call failed', e);
    }
};

/** True when calls should be forwarded to a real SDK (now or once loaded). */
const isLive = () => config.enabled && !config.mockMode && !failed && !foreignInstance;

const flushQueue = () => {
    queue.splice(0).forEach((fn) => safeCall(() => fn(sdk)));
};

const loadSdk = () => {
    import(/* webpackChunkName: "moengage-sdk" */ '@moengage/web-sdk')
        .then((mod) => {
            const moe = (mod && mod.default) || mod;
            const initData = {
                appId: config.appId,
                cluster: config.dataCenter.toUpperCase(),
                env: config.env,
                logLevel: config.logLevel,
                // Web Push is out of scope for now — avoids any service-worker work.
                disableWebPush: true,
            };
            if (config.projectId) initData.projectId = config.projectId;
            moe.initialize(initData);
            sdk = moe;
            loading = false;
            debugInfo(`[MoEngage] initialised (${config.env}, ${config.dataCenter})`);
            flushQueue();
        })
        .catch((e) => {
            loading = false;
            failed = true;
            queue.length = 0;
            debugWarn('[MoEngage] SDK load failed — analytics disabled for this page view', e);
        });
};

/**
 * Initialise MoEngage once. Safe to call more than once; safe at module level.
 * Returns immediately — the SDK download is deferred to browser idle time.
 */
export const initMoEngage = () => {
    try {
        if (!config.enabled || initCalled) return;
        initCalled = true;

        if (config.mockMode) {
            if (config.configErrors.length) {
                debugWarn('[MoEngage] disabled — missing config:', config.configErrors.join('; '));
            } else {
                debugInfo('[MoEngage] disabled — missing config');
            }
            return;
        }
        if (typeof window === 'undefined') return;

        if (window.Moengage || window.moe) {
            // Double initialisation corrupts MoEngage data. Do not init, do not send.
            foreignInstance = true;
            queue.length = 0;
            // Always warn (not just in debug): this needs a human decision.
            // eslint-disable-next-line no-console
            console.warn('[MoEngage] SDK already present on the page (GTM / CDN?). '
                + 'This app will NOT initialise it again and will not send events.');
            return;
        }

        loading = true;
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(loadSdk, { timeout: 3000 });
        } else {
            setTimeout(loadSdk, 1500);
        }
    } catch (e) {
        loading = false;
        debugWarn('[MoEngage] init failed', e);
    }
};

/**
 * Hand an SDK call to MoEngage: run it now if loaded, otherwise queue it.
 * A no-op when disabled, in mock mode, or after a load failure.
 * @param {(sdk: any) => any} fn
 */
export const enqueue = (fn) => {
    try {
        if (!isLive()) return;
        if (sdk) {
            safeCall(() => fn(sdk));
            return;
        }
        if (queue.length >= MAX_QUEUE) queue.shift();
        queue.push(fn);
    } catch (e) {
        debugWarn('[MoEngage] enqueue failed', e);
    }
};

/** Read-only status, for the README / QA console checks. */
export const getClientStatus = () => ({
    enabled: config.enabled,
    mockMode: config.mockMode,
    initCalled,
    loading,
    loaded: !!sdk,
    failed,
    foreignInstance,
    queued: queue.length,
});
